// Eigenbau: sichere Markdown-Leseansicht mit Bildregeln und Mermaid
import { Children, isValidElement, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import type { Components } from "react-markdown";
import { bildSrcFuerAnzeige } from "@/lib/markdown-sicher";
import {
  erstelleUeberschriftSlugger,
  ueberschriftHref,
} from "@/lib/seiten-inhalt";
import { MermaidVorschau } from "./MermaidVorschau";

type Props = {
  markdown: string;
  className?: string;
};

/** Direktiven lesbar machen, ohne Beispiele in Codezäunen anzutasten. */
function bereiteDirektiven(md: string): string {
  let zaun: "```" | "~~~" | null = null;
  return md
    .split("\n")
    .map((zeile) => {
      const zaunTreffer = /^\s*(```|~~~)/.exec(zeile)?.[1] as
        | "```"
        | "~~~"
        | undefined;
      if (zaunTreffer) {
        if (!zaun) zaun = zaunTreffer;
        else if (zaun === zaunTreffer) zaun = null;
        return zeile;
      }
      if (zaun) return zeile;
      return zeile
        .replace(
          /:marke\[((?:\\.|[^\\\]])*)\]\{#([0-9a-f-]{36})\}/gi,
          (_m, zitat: string, id: string) =>
            `«${zitat.replace(/\\]/g, "")}»\`#${id}\``,
        )
        .replace(
          /:verweis\{art=([^\s}]+)\s+kennung=([^\s}]+)\}/gi,
          (_m, art: string, kennung: string) => `**[${art}:${kennung}]**`,
        );
    })
    .join("\n");
}

type MarkdownKnoten = {
  type?: string;
  depth?: number;
  value?: string;
  children?: MarkdownKnoten[];
  data?: {
    hProperties?: Record<string, unknown>;
  };
};

function knotenText(knoten: MarkdownKnoten): string {
  if (typeof knoten.value === "string") return knoten.value;
  return (knoten.children ?? []).map(knotenText).join("");
}

/** Gleiche stabile Anker wie das Inhaltsverzeichnis in der Randspalte. */
function remarkUeberschriftAnker() {
  return (baum: MarkdownKnoten) => {
    const slug = erstelleUeberschriftSlugger();
    for (const knoten of baum.children ?? []) {
      if (knoten.type !== "heading" || ![2, 3].includes(knoten.depth ?? 0)) {
        continue;
      }
      knoten.data = {
        ...knoten.data,
        hProperties: {
          ...knoten.data?.hProperties,
          id: slug(knotenText(knoten)),
        },
      };
    }
  };
}

function textAusKind(knoten: ReactNode): string {
  if (typeof knoten === "string" || typeof knoten === "number") {
    return String(knoten);
  }
  if (Array.isArray(knoten)) return knoten.map(textAusKind).join("");
  if (isValidElement<{ children?: ReactNode }>(knoten)) {
    return textAusKind(knoten.props.children);
  }
  return "";
}

const komponenten: Components = {
  img: ({ src, alt }) => {
    const url = typeof src === "string" ? src : "";
    const { src: sicher, warnung } = bildSrcFuerAnzeige(url);
    if (!sicher) {
      return (
        <span
          role="img"
          aria-label={alt || "Bild"}
          className="seiten-bildwarnung"
        >
          {warnung === "relativ"
            ? `Relatives Bild beibehalten: ${url}`
            : "Unsichere Bildadresse abgewiesen"}
        </span>
      );
    }
    return (
      <img
        src={sicher}
        alt={alt || ""}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
    );
  },
  pre: ({ children }) => {
    const kinder = Children.toArray(children);
    const code = kinder.length === 1 ? kinder[0] : null;
    if (isValidElement<{ className?: string; children?: ReactNode }>(code)) {
      const sprache = /language-([\w-]+)/.exec(code.props.className ?? "")?.[1];
      if (sprache === "mermaid") {
        return (
          <figure className="seiten-diagramm">
            <MermaidVorschau quelle={textAusKind(code.props.children)} />
            <figcaption>Diagramm · Mermaid</figcaption>
          </figure>
        );
      }
    }
    return <pre>{children}</pre>;
  },
  a: ({ href, children }) => {
    if (href?.toLowerCase().startsWith("javascript:")) {
      return <span>{children}</span>;
    }
    const intern = href?.startsWith("#") ?? false;
    if (href && !/^https?:\/\//i.test(href) && !intern) {
      return <span>{children}</span>;
    }
    return (
      <a
        href={intern && href ? ueberschriftHref(href) : href}
        {...(!intern ? { rel: "noopener noreferrer", target: "_blank" } : {})}
      >
        {children}
      </a>
    );
  },
};

export function SeitenLeseansicht({ markdown, className }: Props) {
  return (
    <div className={["seiten-inhalt", className].filter(Boolean).join(" ")}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkUeberschriftAnker]}
        rehypePlugins={[rehypeSanitize]}
        components={komponenten}
      >
        {bereiteDirektiven(markdown)}
      </ReactMarkdown>
    </div>
  );
}
